import { supabase } from '@/integrations/supabase/client';
import { recordServerNow } from '@/shared/lib/serverClock';
// Part 0 (SD-T690): address and key come from the one env module; no per-file fallback.
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '@/shared/config/supabaseEnv';

interface AgriAgentResponse {
  success: boolean;
  response: {
    text: string;
    source: 'faq' | 'cache' | 'openai' | 'anthropic' | 'fallback';
  };
  flag_type: string;
  points_earned: number;
}

interface PhotoAnalysisResponse {
  success: boolean;
  photo_id: string;
  points_earned: number;
  ai_analysis: {
    confidence: number | null;
    for_farmer: {
      status: 'excellent' | 'good' | 'fair' | 'stressed' | 'critical';
      main_finding: string;
      action: 'none' | 'see_agriagent' | 'see_community' | 'contact_supervisor';
      next_check: 'today' | '3_days' | '1_week' | 'next_cycle';
    };
    for_agxl: {
      confidence: number;
      category_confirmed: boolean;
      technical_assessment: string;
      growth_stage: string | null;
      anomaly_flags: string[];
      scoring_signals: Record<string, unknown>;
      input_evidence: Record<string, unknown>;
    };
  } | null;
}

interface VoiceTranscriptionResponse {
  success: boolean;
  transcription?: {
    text: string;
    language: string;
  };
  error?: string;
}

interface CreditScoreResponse {
  success: boolean;
  score: number;
  factors: Record<string, number>;
}

interface NotificationResponse {
  success: boolean;
  id: string;
}

interface KnowledgeSearchResponse {
  success: boolean;
  results: Array<{
    id: string;
    question: string;
    answer: string;
    topic: string;
  }>;
  total: number;
}

const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const { data: { session } } = await supabase.auth.getSession();
  
  // Always include the public key for Edge Function access (env-only, see src/config/supabaseEnv.ts).
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'apikey': SUPABASE_ANON_KEY,
    'Authorization': `Bearer ${session?.access_token || SUPABASE_ANON_KEY}`,
  };

  // IMPORTANT: This app primarily authenticates via a custom session token (PIN flow),
  // which is injected automatically for normal Supabase requests. Since Edge Functions
  // are called via fetch() here, we must forward the token explicitly.
  const sessionToken = (window as any).__AGXL_SESSION_TOKEN__ as string | undefined;
  if (sessionToken) {
    headers['x-session-token'] = sessionToken;
  }
  
  return headers;
};

const callEdgeFunction = async <T>(
  functionName: string,
  body: Record<string, unknown>
): Promise<T> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${SUPABASE_URL}/functions/v1/${functionName}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Edge Function error: ${response.status} - ${errorText}`);
  }
  return response.json();
};

export const agriAgentChat = async (
  userId: string,
  message: string,
  language?: string,
  preferProvider?: 'openai' | 'anthropic'
): Promise<AgriAgentResponse> => {
  return callEdgeFunction<AgriAgentResponse>('agriagent-chat', {
    user_id: userId,
    message,
    language,
    prefer_provider: preferProvider,
  });
};

export const processPhoto = async (
  userId: string,
  photoId: string,
  photoCategory: string,
  storagePath?: string,
  imageUrl?: string,
  cycleId?: string,
  cycleStartDate?: string,
): Promise<PhotoAnalysisResponse> => {
  return callEdgeFunction<PhotoAnalysisResponse>('process-photo', {
    user_id: userId,
    photo_id: photoId,
    photo_category: photoCategory,
    storage_path: storagePath,
    image_url: imageUrl,
    cycle_id: cycleId,
    cycle_start_date: cycleStartDate,
  });
};

export const transcribeVoice = async (
  userId: string,
  audioBase64?: string,
  audioUrl?: string,
  language?: string
): Promise<VoiceTranscriptionResponse> => {
  return callEdgeFunction<VoiceTranscriptionResponse>('voice-transcribe', {
    user_id: userId,
    audio_base64: audioBase64,
    audio_url: audioUrl,
    language,
  });
};

export const calculateCreditScore = async (
  userId: string
): Promise<CreditScoreResponse> => {
  return callEdgeFunction<CreditScoreResponse>('calculate-credit-score', {
    user_id: userId,
  });
};

// ── Durable gamification Bonus-layer award (shared CCS + CBS) ──────────────
// Resolves the point value from points_config by the farmer's country + the
// event's crop (precedence: crop-specific → country default → catalogue), then
// awards it into credit_scores.bonus_score server-side: permanent, idempotent
// (unique per user+event_key), capped at the country+crop gamification ceiling.
// Identity is bound from the session token inside the edge function; the client
// never reads points_config nor writes bonus_score directly. Never throws — a
// failed/duplicate award must not break the action that triggered it.
export interface AwardPointsResult {
  success: boolean;
  duplicate?: boolean;
  awarded: number;
  points_value?: number;
  bonus_score?: number;
  total_score?: number;
  crop_id?: string | null;
  cap?: number;
  capped?: boolean;
  error?: string;
  /** A2 (R17): server clock at reply time (ISO UTC). */
  server_now?: string;
}

export const awardPoints = async (
  actionKey: string,
  opts?: {
    referenceType?: 'photo' | 'activity' | 'daily_record' | 'network_answer' | 'other' | null;
    referenceId?: string | null;
    /** Idempotency key; defaults server-side to `${actionKey}:${referenceId}`.
     *  Pass an explicit key for reference-less events (e.g. daily login by day). */
    eventKey?: string | null;
  }
): Promise<AwardPointsResult | null> => {
  try {
    const result = await callEdgeFunction<AwardPointsResult>('award-points', {
      action_key: actionKey,
      reference_type: opts?.referenceType ?? null,
      reference_id: opts?.referenceId ?? null,
      event_key: opts?.eventKey ?? null,
    });
    recordServerNow(result?.server_now, 'award-points');
    return result;
  } catch (err) {
    console.warn('[awardPoints] failed (non-blocking):', err);
    return null;
  }
};

export const sendNotification = async (
  userId: string,
  type: string,
  title: string,
  body: string,
  data?: Record<string, unknown>
): Promise<NotificationResponse> => {
  return callEdgeFunction<NotificationResponse>('send-notification', {
    user_id: userId,
    type,
    title,
    body,
    data,
  });
};

export const searchKnowledgeBase = async (
  query: string,
  countryId?: string,
  cropId?: string,
  language?: string
): Promise<KnowledgeSearchResponse> => {
  return callEdgeFunction<KnowledgeSearchResponse>('search-knowledge-base', {
    query,
    country_id: countryId,
    crop_id: cropId,
    language,
  });
};

export const syncOfflineData = async (
  userId: string,
  activities: any[],
  photos: any[]
): Promise<{ success: boolean; synced: { activities: number; photos: number } }> => {
  return callEdgeFunction('sync-offline-data', {
    user_id: userId,
    activities,
    photos,
  });
};

export const completeOnboarding = async (
  userId: string,
  data: {
    full_name?: string;
    country_id?: string;
    region?: string;
    village?: string;
    primary_crop_id?: string;
    farm_size_hectares?: number;
    farming_experience_years?: number;
    language?: string;
    gender?: string;
    sales_channel?: string;
    consents?: Array<{ consent_type: string; is_granted: boolean; collection_method: string }>;
    /** Blanket AAK data-sharing consent JSONB — written server-side (service role)
     *  so it lands reliably (the trg_sync_consent_level trigger derives the level). */
    aak_consent_details?: Record<string, boolean>;
  }
): Promise<{ success: boolean; consents_recorded?: number }> => {
  return callEdgeFunction('complete-onboarding', {
    user_id: userId,
    ...data,
  });
};

export const exportCreditReport = async (
  userId: string,
  format?: 'json' | 'pdf'
): Promise<{ success: boolean; report: any }> => {
  return callEdgeFunction('export-credit-report', {
    user_id: userId,
    format,
  });
};

export const checkAndAwardBadges = async (
  userId: string,
  farmerCycleId?: string
): Promise<{ success: boolean; badges_awarded: string[] }> => {
  return callEdgeFunction('award-badge', {
    user_id: userId,
    farmer_cycle_id: farmerCycleId,
  });
};

export const verifyYield = async (
  farmerCycleId: string,
  actualYieldKg: number
): Promise<{ success: boolean; accuracy_percent: number }> => {
  return callEdgeFunction('verify-yield', {
    farmer_cycle_id: farmerCycleId,
    actual_yield_kg: actualYieldKg,
  });
};

export const textToSpeech = async (
  userId: string,
  text: string,
  language: string
): Promise<{ success: boolean; audio_base64?: string; error?: string }> => {
  return callEdgeFunction<{ success: boolean; audio_base64?: string; error?: string }>('voice-tts', {
    user_id: userId,
    text: text,
    language: language,
  });
};

export interface VoiceOrchestratorResponse {
  success: boolean;
  transcription: { text: string; language: string } | null;
  response: { text: string; source: string } | null;
  // FIX-CCS-MULTI-5A (SD-8): `available` is false when TTS failed and only text was
  // produced. `audio_available` mirrors it at the top level for convenience.
  tts: { audio_url: string | null; provider: string; available?: boolean } | null;
  audio_available?: boolean;
  error_code?: 'NO_SPEECH' | 'LOW_CONFIDENCE' | 'QUOTA_EXCEEDED';
  error_audio_url?: string;
  conversation_type: string | null;
  points_earned: number | null;
  error: string | null;
  processing_time_ms: number;
}

export const voiceOrchestrate = async (
  conversationId: string,
  audioUrl: string,
  language: string,
  userId: string
): Promise<VoiceOrchestratorResponse> => {
  return callEdgeFunction<VoiceOrchestratorResponse>('voice-orchestrator', {
    conversation_id: conversationId,
    audio_url: audioUrl,
    language: language,
    user_id: userId,
  });
};

export interface PeerLearningSyncParams {
  action: 'get_questions' | 'get_replies' | 'get_learn';
  queryText?: string;
  questionId?: string;
  /** Community Fixes 0710 Part B2.3: replies to an organisation voice note (the function re-checks the org). */
  orgNoteId?: string;
  countryId?: string;
  caller?: string;
  // FIX-CCS-COMMUNITY Phase 1.2 — documented token contract. The requesting farmer is
  // resolved SERVER-SIDE from this token (never from a client-supplied user id). The
  // body field is the primary mechanism, matching submit-grievance / revoke-consent;
  // callEdgeFunction also attaches the same value as the x-session-token header, which
  // the function accepts as a fallback (matching record-consent).
  // NOTE: `cropId` was removed. It was accepted and silently ignored by the function
  // while the write side never populated network_questions.crop_id. The write side now
  // populates crop_id, so crop filtering can be reintroduced deliberately later.
  session_token?: string;
}

export interface PeerLearningSyncResponse {
  success?: boolean;
  questions?: any[];
  replies?: any[];
  learn_content?: any[];
  // FIX-CCS-COMMUNITY Phase 1.3 — `suppressed` distinguishes "the k-anonymity gate hid
  // other farmers' content" from "the community genuinely has no questions". The client
  // MUST branch on this boolean. `note` is for logs only and is never rendered.
  suppressed?: boolean;
  note?: string;
  is_trending?: boolean;
  min_distinct_authors?: number;
  distinct_authors?: number;
}

export const peerLearningSync = async (
  params: PeerLearningSyncParams
): Promise<PeerLearningSyncResponse> => {
  const sessionToken = (window as any).__AGXL_SESSION_TOKEN__ as string | undefined;
  const body: Record<string, unknown> = { ...params };
  if (sessionToken && !body.session_token) {
    body.session_token = sessionToken;
  }
  return callEdgeFunction<PeerLearningSyncResponse>('peer-learning-sync', body);
};

// Community Fixes 0710 Part H.2 (D14): a video reply goes through community-video-reply, the only writer of the
// private community-videos bucket (content-checked, folder bound to the session farmer). Multipart, so it cannot
// use callEdgeFunction's JSON body. Errors carry the server's machine code; the screen owns the wording.
export class CommunityVideoError extends Error {
  constructor(public code: string, public status: number) {
    super(code);
  }
}

export const postCommunityVideoReply = async (
  questionId: string,
  file: Blob,
  durationSeconds: number,
): Promise<{ answerId: string; sizeBytes: number }> => {
  const headers = await getAuthHeaders();
  delete headers['Content-Type']; // the browser sets the multipart boundary
  const form = new FormData();
  form.append('question_id', questionId);
  form.append('duration_seconds', String(Math.max(1, Math.round(durationSeconds))));
  form.append('file', file, 'reply');
  const response = await fetch(`${SUPABASE_URL}/functions/v1/community-video-reply`, { method: 'POST', headers, body: form });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload?.answer_id) {
    throw new CommunityVideoError(String(payload?.error ?? 'INTERNAL'), response.status);
  }
  return { answerId: payload.answer_id, sizeBytes: payload.size_bytes };
};

// C-02b: grievance submission
export const submitGrievance = async (
  sessionToken: string,
  category: string,
  subject: string,
  description: string,
): Promise<{ success: boolean; ticket_id?: string; ticket_reference?: string; error?: string }> => {
  return callEdgeFunction('submit-grievance', {
    session_token: sessionToken,
    category,
    subject,
    description,
  });
};

// C-08: consent revocation
export const revokeConsent = async (
  sessionToken: string,
  consentType: string,
): Promise<{ success: boolean; revoked_consent_type?: string; error?: string }> => {
  return callEdgeFunction('revoke-consent', {
    session_token: sessionToken,
    consent_type: consentType,
  });
};

// Consent GRANT / re-affirm path (companion to revokeConsent). Upserts one row
// per (user, purpose) at the current active terms version, server-side and
// session-token bound (respects unique_user_consent_type). Used by the
// per-purpose withdrawal screen's re-grant control (one purpose) and the
// login-time re-consent gate (all nine purposes; marketing unbundled).
export interface ConsentGrantInput {
  consent_type: string;
  is_granted: boolean;
}
export const recordConsent = async (
  sessionToken: string,
  consents: ConsentGrantInput[],
  collectionMethod: string = 'in_app_prompt',
): Promise<{ success: boolean; consents_recorded?: number; consent_version?: string | null; error?: string }> => {
  return callEdgeFunction('record-consent', {
    session_token: sessionToken,
    consents,
    collection_method: collectionMethod,
  });
};

// WAP Phase 1 (P1.3): bridge claim-ticket exchange. Trades a one-time `code`
// (from the bank wrapper's /bridge?code=… deep link) for the CCS session_token
// it carries. Throws via callEdgeFunction on any non-200 (unknown/expired/used
// code) so the /bridge route can fall back to PIN login.
export interface BridgeExchangeResponse {
  success: boolean;
  session_token: string;
  user_id: string;
  client_id: string | null;
}

export const bridgeExchange = async (code: string): Promise<BridgeExchangeResponse> => {
  return callEdgeFunction<BridgeExchangeResponse>('bridge-exchange', { code });
};

// WAP Phase 2 (S4.1): per-bank runtime branding. Session-bound — the edge fn resolves
// the caller's org from x-session-token only and returns that org's branding (or null
// for org-less / unbranded farmers → generic CCS default). No cross-org access.
export interface OrgBrandingResponse {
  organization_id: string | null;
  branding: {
    primary_color: string | null;
    accent_color: string | null;
    logo_text: string | null;
    logo_url: string | null;
  } | null;
}

export const getOrgBranding = async (): Promise<OrgBrandingResponse> => {
  return callEdgeFunction<OrgBrandingResponse>('org-branding', {});
};
